import { authedFetch, API_BASE } from '@/lib/api-client';
import { ensureOk } from '@/lib/api-error';
import type { components } from '@/api/types';

type Schemas = components['schemas'];

export type MeResponse = Schemas['MeResponse'];
export type ProjectResponse = Schemas['ProjectResponse'];
export type TimelineResponse = Schemas['TimelineResponse'];
export type TimelineEpicResponse = Schemas['TimelineEpicResponse'];
export type TimelineTaskResponse = Schemas['TimelineTaskResponse'];
export type TaskResponse = Schemas['TaskResponse'];
export type WorkloadResponse = Schemas['WorkloadResponse'];
export type UserWorkloadResponse = Schemas['UserWorkloadResponse'];
export type WeekLoadResponse = Schemas['WeekLoadResponse'];
export type WorkloadContributionsResponse = Schemas['WorkloadContributionsResponse'];
export type WorkloadLeaveResponse = Schemas['WorkloadLeaveResponse'];
export type TaskContributionResponse = Schemas['TaskContributionResponse'];
export type MemberResponse = Schemas['MemberResponse'];
export type TagResponse = Schemas['TagResponse'];
export type DeploymentResponse = Schemas['DeploymentResponse'];
export type EpicResponse = Schemas['EpicResponse'];
export type CreateTaskRequest = Schemas['CreateTaskRequest'];
export type CreateTaskResponse = Schemas['CreateTaskResponse'];
export type CreateTaskAssignee = Schemas['CreateTaskAssignee'];
export type UpdateTaskRequest = Schemas['UpdateTaskRequest'];
export type AddAssigneeRequest = Schemas['AddAssigneeRequest'];
export type DevLoginRequest = Schemas['DevLoginRequest'];
export type CreateProjectRequest = Schemas['CreateProjectRequest'];
export type UpdateProjectRequest = Schemas['UpdateProjectRequest'];
export type AddMemberRequest = Schemas['AddMemberRequest'];
export type AddMemberResponse = Schemas['AddMemberResponse'];
export type ChangeMemberRoleRequest = Schemas['ChangeMemberRoleRequest'];
export type PermissionGroup = Schemas['PermissionGroup'];
export type ProjectRoleResponse = Schemas['ProjectRoleResponse'];
export type CreateProjectRoleRequest = Schemas['CreateProjectRoleRequest'];
export type UpdateProjectRoleRequest = Schemas['UpdateProjectRoleRequest'];
export type CreateTagRequest = Schemas['CreateTagRequest'];
export type UpdateTagRequest = Schemas['UpdateTagRequest'];
export type CreateEpicRequest = Schemas['CreateEpicRequest'];
export type UpdateEpicRequest = Schemas['UpdateEpicRequest'];
export type UpdateEpicStatusRequest = Schemas['UpdateEpicStatusRequest'];
export type EpicStatus = Schemas['EpicStatus'];
export type UpdateDeploymentStatusRequest = Schemas['UpdateDeploymentStatusRequest'];
export type CreateDeploymentRequest = Schemas['CreateDeploymentRequest'];
export type AttachTasksRequest = Schemas['AttachTasksRequest'];
export type UpdateTaskStatusRequest = Schemas['UpdateTaskStatusRequest'];
export type TaskStatusResponse = Schemas['TaskStatusResponse'];
export type CreateTaskStatusRequest = Schemas['CreateTaskStatusRequest'];
// NOTE: TaskStatus enum has been removed — task statuses are now per-project, stored in the
// `task_statuses` table. Use TaskStatusResponse from the API instead of a static enum.
export type DeploymentStatus = Schemas['DeploymentStatus'];
export type SprintResponse = Schemas['SprintResponse'];
export type UpdateSprintRequest = Schemas['UpdateSprintRequest'];
export type AddSprintRequest = Schemas['AddSprintRequest'];
export type HolidayResponse = Schemas['HolidayResponse'];
export type CreateHolidayRequest = Schemas['CreateHolidayRequest'];
export type UserResponse = Schemas['UserResponse'];
export type UpdateUserDisplayNameRequest = Schemas['UpdateUserDisplayNameRequest'];
export type CommentResponse = Schemas['CommentResponse'];
export type CreateCommentRequest = Schemas['CreateCommentRequest'];
export type UpdateCommentRequest = Schemas['UpdateCommentRequest'];

/** List shape returned by `GET /projects/{id}/notes` — body_md is omitted. */
export type NoteListResponse = Schemas['NoteListResponse'];
/** Detail shape returned by `GET /notes/{id}` — includes body_md. */
export type NoteResponse = Schemas['NoteResponse'];
export type CreateNoteRequest = Schemas['CreateNoteRequest'];
/** `parent_id`: omit to keep, `null` to move to root, a uuid to re-parent (subtree moves too). */
export type UpdateNoteRequest = Schemas['UpdateNoteRequest'];
// Reorder one sibling group. `parent_id: null` targets the root-level notes;
// `ordered_ids` is the group's full member list in the desired order.
export type ReorderNotesRequest = Schemas['ReorderNotesRequest'];
/** Version metadata (body omitted) — `GET /notes/{id}/versions[/latest]`. */
export type NoteVersionListResponse = Schemas['NoteVersionListResponse'];

// Image-attachment types. Hand-declared to match the backend's task/note
// attachment endpoints. Task (`AttachmentResponse`) and note
// (`NoteAttachmentResponse`) uploads return the same shape — the FE only
// consumes `url`, a relative path that drops straight into the Markdown.
export type AttachmentResponse = {
  id: string;
  url: string;
  filename: string;
  content_type: string;
  byte_size: number;
};

// Leave types. Hand-declared until `pnpm api:generate` is rerun against the
// backend's `/users/{id}/leaves` endpoints (shipped on `main`, migration
// 000011). Replace these with `Schemas['LeaveResponse']` etc. when the
// generated types arrive.
export type LeaveKind =
  | 'SickLeave'
  | 'BusinessLeave'
  | 'TrainingLeave'
  | 'MaternityLeave'
  | 'VacationLeave';
export type LeaveDuration = 'HalfDay' | 'FullDay';
export type LeaveResponse = {
  id: string;
  user_id: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  kind: LeaveKind;
  duration: LeaveDuration;
  note: string | null;
  created_at: string;
  created_by: string;
};
export type CreateLeaveRequest = {
  start_date: string;
  end_date: string;
  kind: LeaveKind;
  duration: LeaveDuration;
  note?: string;
};
export type UpdateLeaveRequest = Schemas['UpdateLeaveRequest'];
export type LeaveSummaryResponse = Schemas['LeaveSummaryResponse'];

// Risk & call-out types. Hand-declared pending `pnpm api:generate` against
// the /tasks/:id/risk-callout endpoint. Replace with `Schemas['...']` once
// the generated types land.
// Hand-declared pending `pnpm api:generate` against the /tasks/:id/links
// endpoints. Replace with `Schemas['LinkTaskRequest']` once generated.
export type LinkTaskRequest = {
  linked_task_id: string;
};

export type TaskRiskCalloutResponse = {
  week_id: string | null;
  week_start: string | null; // YYYY-MM-DD
  week_end: string | null; // YYYY-MM-DD
  callouts: string | null;
  risk_probability: number | null;
  risk_impact: number | null;
};
export type UpsertTaskRiskCalloutRequest = {
  callouts?: string | null;
  risk_probability?: number | null;
  risk_impact?: number | null;
};

export type TimelineQuery = {
  tag_id?: string;
  date_from?: string;
  date_to?: string;
};

export type ListTasksQuery = {
  status_id?: string;
  tag_id?: string;
  assignee_id?: string;
  date_from?: string;
  date_to?: string;
};

export type WorkloadQuery = {
  project_id: string;
  from: string;
  to: string;
};

export type WorkloadContributionsQuery = {
  user_id: string;
  week_start: string; // ISO Monday (YYYY-MM-DD)
  project_id: string;
};

// Prepend the backend mount prefix. Call sites keep their bare route literals
// (`/projects`, `/me`, …) so they read like the backend spec; the prefix lives
// in exactly one place (`API_BASE`).
function url(path: string): string {
  return `${API_BASE}${path}`;
}

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

async function parseJson<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) {
    const preview = (await res.text().catch(() => '')).slice(0, 120);
    throw new Error(
      `Expected JSON from ${res.url} but got "${contentType || 'no content-type'}". ` +
        `Body starts with: ${preview || '(empty)'}. ` +
        `Likely cause: request was not proxied to the backend (check vite.config.ts).`,
    );
  }
  return res.json() as Promise<T>;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await authedFetch(url(path)).then(ensureOk);
  return parseJson<T>(res);
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  const res = await authedFetch(url(path), {
    method: 'PATCH',
    body: JSON.stringify(body),
  }).then(ensureOk);
  return parseJson<T>(res);
}

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await authedFetch(url(path), {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(ensureOk);
  return parseJson<T>(res);
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  // `authedFetch` sends `FormData` as multipart without forcing a JSON
  // content-type and still adds the CSRF header — no extra plumbing needed.
  const res = await authedFetch(url(path), { method: 'POST', body: form }).then(ensureOk);
  return parseJson<T>(res);
}

async function postVoid(path: string, body?: unknown): Promise<void> {
  await authedFetch(url(path), {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(ensureOk);
}

async function putVoid(path: string, body?: unknown): Promise<void> {
  await authedFetch(url(path), {
    method: 'PUT',
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(ensureOk);
}

async function patchVoid(path: string, body?: unknown): Promise<void> {
  await authedFetch(url(path), {
    method: 'PATCH',
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(ensureOk);
}

async function putJson<T>(path: string, body?: unknown): Promise<T> {
  const res = await authedFetch(url(path), {
    method: 'PUT',
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(ensureOk);
  return parseJson<T>(res);
}

async function deleteVoid(path: string): Promise<void> {
  await authedFetch(url(path), { method: 'DELETE' }).then(ensureOk);
}

async function deleteVoidWithBody(path: string, body: unknown): Promise<void> {
  await authedFetch(url(path), {
    method: 'DELETE',
    body: JSON.stringify(body),
  }).then(ensureOk);
}

export const api = {
  devLogin: async (body: DevLoginRequest): Promise<void> => {
    const res = await fetch(url('/auth/dev-login'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(ensureOk);
    if (res.status !== 204) await res.text().catch(() => '');
  },

  getMe: () => getJson<MeResponse>('/me'),

  /**
   * Like `getMe`, but never bounces the user to /sign-in on 401. Used by
   * the public homepage to decide whether to render itself or hand off to
   * /projects, without dragging an unauthenticated visitor through the login
   * round-trip.
   */
  getMeSilently: async (): Promise<MeResponse | null> => {
    try {
      const res = await authedFetch(url('/me'), {}, { redirectOnAuthFailure: false });
      if (!res.ok) return null;
      return await parseJson<MeResponse>(res);
    } catch {
      return null;
    }
  },

  listProjects: () => getJson<ProjectResponse[]>('/projects'),

  createProject: (body: CreateProjectRequest) => postJson<ProjectResponse>('/projects', body),

  updateProject: (projectId: string, body: UpdateProjectRequest) =>
    patchJson<ProjectResponse>(`/projects/${projectId}`, body),

  archiveProject: (projectId: string) => postVoid(`/projects/${projectId}/archive`),

  createEpic: (projectId: string, body: CreateEpicRequest) =>
    postJson<EpicResponse>(`/projects/${projectId}/epics`, body),

  updateEpic: (epicId: string, body: UpdateEpicRequest) =>
    patchJson<EpicResponse>(`/epics/${epicId}`, body),

  updateEpicStatus: (epicId: string, body: UpdateEpicStatusRequest) =>
    patchJson<EpicResponse>(`/epics/${epicId}/status`, body),

  deleteEpic: (epicId: string) => deleteVoid(`/epics/${epicId}`),

  // Manual ordering. The body carries the full ordered id list for the scope;
  // the backend assigns position = index (see interface/ordering.md). Bodies are
  // hand-typed here because they predate `pnpm api:generate` against the
  // reorder endpoints — swap to `Schemas[...]` once the generated types arrive.
  reorderEpics: (projectId: string, epicIds: string[]) =>
    putVoid(`/projects/${projectId}/epics/order`, { epic_ids: epicIds }),

  reorderTasks: (epicId: string, taskIds: string[]) =>
    putVoid(`/epics/${epicId}/tasks/order`, { task_ids: taskIds }),

  getTimeline: (projectId: string, query: TimelineQuery = {}) =>
    getJson<TimelineResponse>(`/projects/${projectId}/timeline${qs(query)}`),

  listProjectTasks: (projectId: string, query: ListTasksQuery = {}) =>
    getJson<TaskResponse[]>(`/projects/${projectId}/tasks${qs(query)}`),

  updateTaskStatus: (taskId: string, body: { status_id: string }) =>
    patchJson<TaskResponse>(`/tasks/${taskId}/status`, body),

  getWorkload: (query: WorkloadQuery) => getJson<WorkloadResponse>(`/workload${qs(query)}`),

  // Per-task breakdown of one (user, week) workload cell — the reverse of the
  // aggregation `getWorkload` performs. Path stays under the already-proxied
  // `/workload` prefix, so no vite.config.ts change is needed (CLAUDE.md §4.7).
  getWorkloadContributions: (query: WorkloadContributionsQuery) =>
    getJson<WorkloadContributionsResponse>(`/workload/contributions${qs(query)}`),

  listMembers: (projectId: string) => getJson<MemberResponse[]>(`/projects/${projectId}/members`),

  addMember: (projectId: string, body: AddMemberRequest) =>
    postJson<AddMemberResponse>(`/projects/${projectId}/members`, body),

  changeMemberRole: (projectId: string, userId: string, body: ChangeMemberRoleRequest) =>
    patchJson<MemberResponse>(`/projects/${projectId}/members/${userId}`, body),

  removeMember: (projectId: string, userId: string) =>
    deleteVoid(`/projects/${projectId}/members/${userId}`),

  listProjectTags: (projectId: string) => getJson<TagResponse[]>(`/projects/${projectId}/tags`),

  createTag: (projectId: string, body: CreateTagRequest) =>
    postJson<TagResponse>(`/projects/${projectId}/tags`, body),

  updateTag: (tagId: string, body: UpdateTagRequest) =>
    patchJson<TagResponse>(`/tags/${tagId}`, body),

  deleteTag: (tagId: string) => deleteVoid(`/tags/${tagId}`),

  listEpicDeployments: (epicId: string) =>
    getJson<DeploymentResponse[]>(`/epics/${epicId}/deployments`),

  createDeployment: (epicId: string, body: CreateDeploymentRequest) =>
    postJson<DeploymentResponse>(`/epics/${epicId}/deployments`, body),

  approveDeployment: (deploymentId: string) =>
    postJson<DeploymentResponse>(`/deployments/${deploymentId}/approve`),

  updateDeploymentStatus: (deploymentId: string, body: UpdateDeploymentStatusRequest) =>
    patchJson<DeploymentResponse>(`/deployments/${deploymentId}/status`, body),

  deleteDeployment: (deploymentId: string) => deleteVoid(`/deployments/${deploymentId}`),

  attachDeploymentTasks: (deploymentId: string, body: AttachTasksRequest) =>
    postJson<DeploymentResponse>(`/deployments/${deploymentId}/tasks`, body),

  detachDeploymentTask: (deploymentId: string, taskId: string) =>
    deleteVoid(`/deployments/${deploymentId}/tasks/${taskId}`),

  listProjectEpics: (projectId: string) => getJson<EpicResponse[]>(`/projects/${projectId}/epics`),

  listEpicTasks: (epicId: string) => getJson<TaskResponse[]>(`/epics/${epicId}/tasks`),

  createTask: (epicId: string, body: CreateTaskRequest) =>
    postJson<CreateTaskResponse>(`/epics/${epicId}/tasks`, body),

  updateTask: (taskId: string, body: UpdateTaskRequest) =>
    patchJson<TaskResponse>(`/tasks/${taskId}`, body),

  addTaskAssignee: (taskId: string, body: AddAssigneeRequest) =>
    postVoid(`/tasks/${taskId}/assignees`, body),

  deleteTask: (taskId: string) => deleteVoid(`/tasks/${taskId}`),

  removeTaskAssignee: (taskId: string, userId: string) =>
    deleteVoid(`/tasks/${taskId}/assignees/${userId}`),

  assignTaskTag: (taskId: string, tagId: string) =>
    postVoid(`/tasks/${taskId}/tags`, { tag_id: tagId }),

  unassignTaskTag: (taskId: string, tagId: string) => deleteVoid(`/tasks/${taskId}/tags/${tagId}`),

  linkTask: (taskId: string, linkedTaskId: string) =>
    postJson<TaskResponse>(`/tasks/${taskId}/links`, { linked_task_id: linkedTaskId }),

  unlinkTask: (taskId: string, linkedTaskId: string) =>
    deleteVoid(`/tasks/${taskId}/links/${linkedTaskId}`),

  assignEpicTag: (epicId: string, tagId: string) =>
    postVoid(`/epics/${epicId}/tags`, { tag_id: tagId }),

  unassignEpicTag: (epicId: string, tagId: string) => deleteVoid(`/epics/${epicId}/tags/${tagId}`),

  listSprints: (projectId: string) => getJson<SprintResponse[]>(`/projects/${projectId}/sprints`),

  regenerateSprints: (projectId: string) =>
    postJson<SprintResponse[]>(`/projects/${projectId}/sprints/regenerate`),

  addSprint: (projectId: string, body: AddSprintRequest) =>
    postJson<SprintResponse[]>(`/projects/${projectId}/sprints`, body),

  updateSprint: (sprintId: string, body: UpdateSprintRequest) =>
    patchJson<SprintResponse[]>(`/sprints/${sprintId}`, body),

  listHolidays: (projectId: string) =>
    getJson<HolidayResponse[]>(`/projects/${projectId}/holidays`),

  createHoliday: (projectId: string, body: CreateHolidayRequest) =>
    postJson<HolidayResponse>(`/projects/${projectId}/holidays`, body),

  deleteHoliday: (holidayId: string) => deleteVoid(`/holidays/${holidayId}`),

  listLeaves: (userId: string) => getJson<LeaveResponse[]>(`/users/${userId}/leaves`),

  getLeaveSummary: (userId: string, year: number) =>
    getJson<LeaveSummaryResponse>(`/users/${userId}/leaves/summary${qs({ year })}`),

  listProjectLeaves: (projectId: string) =>
    getJson<LeaveResponse[]>(`/projects/${projectId}/leaves`),

  createLeave: (userId: string, body: CreateLeaveRequest) =>
    postJson<LeaveResponse>(`/users/${userId}/leaves`, body),

  updateLeave: (leaveId: string, body: UpdateLeaveRequest) =>
    patchJson<LeaveResponse>(`/leaves/${leaveId}`, body),

  deleteLeave: (leaveId: string) => deleteVoid(`/leaves/${leaveId}`),

  updateUserDisplayName: (userId: string, body: UpdateUserDisplayNameRequest) =>
    patchJson<UserResponse>(`/users/${userId}`, body),

  // Prefix-search real (non-stub) users by email or name for the add-member
  // typeahead. The `/users` route isn't in the generated openapi-fetch types
  // yet — rerun `pnpm api:generate` to pick it up; the response shape is the
  // already-generated `UserResponse`.
  searchUsers: (query: string, limit?: number) =>
    getJson<UserResponse[]>(`/users${qs({ q: query, limit })}`),

  listTaskComments: (taskId: string) => getJson<CommentResponse[]>(`/tasks/${taskId}/comments`),

  createTaskComment: (taskId: string, body: CreateCommentRequest) =>
    postJson<CommentResponse>(`/tasks/${taskId}/comments`, body),

  updateComment: (commentId: string, body: UpdateCommentRequest) =>
    patchJson<CommentResponse>(`/comments/${commentId}`, body),

  deleteComment: (commentId: string) => deleteVoid(`/comments/${commentId}`),

  getTaskRiskCallout: (taskId: string) =>
    getJson<TaskRiskCalloutResponse>(`/tasks/${taskId}/risk-callout`),

  getTaskRiskCalloutHistory: (taskId: string) =>
    getJson<TaskRiskCalloutResponse[]>(`/tasks/${taskId}/risk-callout/history`),

  putTaskRiskCallout: (taskId: string, body: UpsertTaskRiskCalloutRequest) =>
    putJson<TaskRiskCalloutResponse>(`/tasks/${taskId}/risk-callout`, body),

  listNotes: (projectId: string) => getJson<NoteListResponse[]>(`/projects/${projectId}/notes`),

  getNote: (noteId: string) => getJson<NoteResponse>(`/notes/${noteId}`),

  // Newest content version's metadata — who last edited the note and when.
  getLatestNoteVersion: (noteId: string) =>
    getJson<NoteVersionListResponse>(`/notes/${noteId}/versions/latest`),

  getTask: (taskId: string) => getJson<TaskResponse>(`/tasks/${taskId}`),

  createNote: (projectId: string, body: CreateNoteRequest) =>
    postJson<NoteResponse>(`/projects/${projectId}/notes`, body),

  updateNote: (noteId: string, body: UpdateNoteRequest) =>
    patchJson<NoteResponse>(`/notes/${noteId}`, body),

  // Sibling reorder. Sends the full ordered id list for one group
  // (`parent_id: null` = root level); the backend assigns position = index.
  // Path stays under the already-proxied `/projects/.../notes` prefix.
  reorderNotes: (projectId: string, body: ReorderNotesRequest) =>
    patchVoid(`/projects/${projectId}/notes/reorder`, body),

  // Upload an image against a task; returns the attachment metadata whose `url`
  // (a relative path) is embedded in the task's Markdown. Multipart field is
  // `file`, mirroring the Excel-import handler's pattern. Requires task-editor
  // authority server-side; only available once the task exists (edit mode).
  uploadTaskAttachment: (taskId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return postForm<AttachmentResponse>(`/tasks/${taskId}/attachments`, form);
  },

  // Upload an image against a note; returns metadata whose `url` is embedded in
  // the note body Markdown. Same multipart `file` field and response shape as
  // task attachments. Requires note-edit authority server-side.
  uploadNoteAttachment: (noteId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return postForm<AttachmentResponse>(`/notes/${noteId}/attachments`, form);
  },

  deleteNote: (noteId: string) => deleteVoid(`/notes/${noteId}`),

  // Task status configuration endpoints (per-project).
  // Hand-declared since api:generate cannot run against the backend from this environment.
  listTaskStatuses: (projectId: string) =>
    getJson<TaskStatusResponse[]>(`/projects/${projectId}/task-statuses`),

  createTaskStatus: (projectId: string, body: CreateTaskStatusRequest) =>
    postJson<TaskStatusResponse>(`/projects/${projectId}/task-statuses`, body),

  updateTaskStatusConfig: (statusId: string, body: UpdateTaskStatusRequest) =>
    patchJson<TaskStatusResponse>(`/task-statuses/${statusId}`, body),

  reorderTaskStatuses: (projectId: string, order: { id: string; sort_order: number }[]) =>
    putVoid(`/projects/${projectId}/task-statuses/order`, { order }),

  deleteTaskStatus: (statusId: string, replacementStatusId: string) =>
    deleteVoidWithBody(`/task-statuses/${statusId}`, {
      replacement_status_id: replacementStatusId,
    }),

  // The project's role catalog. Role identity is per-project data now — a role's
  // `name` is both the member-list label and the status-group header, and
  // `permission_group` is what any capability check keys off.
  listProjectRoles: (projectId: string) =>
    getJson<ProjectRoleResponse[]>(`/projects/${projectId}/roles`),

  createProjectRole: (projectId: string, body: CreateProjectRoleRequest) =>
    postJson<ProjectRoleResponse>(`/projects/${projectId}/roles`, body),

  updateProjectRole: (projectId: string, roleId: string, body: UpdateProjectRoleRequest) =>
    patchJson<ProjectRoleResponse>(`/projects/${projectId}/roles/${roleId}`, body),

  // Soft-delete. 409s while any member still holds the role, so reassign them
  // first. Statuses filed under the role fall back to ungrouped server-side.
  deleteProjectRole: (projectId: string, roleId: string) =>
    deleteVoid(`/projects/${projectId}/roles/${roleId}`),
};
